import { Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import {
  TABULAR,
  homeColor,
  homeCardStyle,
  homePastel,
  homeText
} from "../home-theme.ts"
import {
  ClipboardCheckIcon,
  CircleAlertIcon,
  DollarSignIcon,
  FolderIcon,
  InfoIcon,
  ReceiptTextIcon
} from "./home-icons.tsx"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { Calendar } from "../../../Icon/icons/calendar"

type Glyph = React.FC<React.SVGProps<SVGSVGElement>>

export interface TodayCount {
  key: string
  label: string
  /** `null` means the metric did not resolve — the tile is omitted, not zeroed. */
  value: number | null
  /** Pre-formatted display text; the raw `value` still decides presence. */
  display?: string
  /** Money and overdue figures read red when they are not good news. */
  valueColor?: string
  icon: Glyph
  tint: { bg: string; fg: string }
  /** Explains what is counted. Required for Need attention (§4). */
  explanation?: string
  drillTo?: string
}

// One pastel per tile, so a row of counts reads as four different things
// rather than four copies of one tile. Need attention keeps a warm tint.
const TEAL = homePastel.teal
const AMBER = homePastel.peach
const RED = homePastel.rose

export const todayCountSpecs = (
  t: (key: string, fallback: string) => string,
  counts: {
    activeProjects: number | null
    needAttention: number | null
    milestonesThisWeek: number | null
    pendingApprovals: number | null
  },
  explanation: string
): TodayCount[] => [
  {
    key: "active_projects",
    label: t("home.today.activeProjects", "Active projects"),
    value: counts.activeProjects,
    icon: FolderIcon,
    tint: homePastel.mint,
    drillTo: "/projects"
  },
  {
    key: "need_attention",
    label: t("home.today.needAttention", "Need attention"),
    value: counts.needAttention,
    icon: CircleAlertIcon,
    tint: AMBER,
    explanation
  },
  {
    key: "milestones_this_week",
    label: t("home.today.milestonesThisWeek", "Milestones this week"),
    value: counts.milestonesThisWeek,
    icon: Calendar as Glyph,
    tint: homePastel.sky
  },
  {
    key: "pending_approvals",
    label: t("home.today.pendingApprovals", "Pending approvals"),
    value: counts.pendingApprovals,
    icon: ClipboardCheckIcon,
    tint: homePastel.lilac,
    // The backend's `pending_approvals` is the company's approval queue; the
    // Approvals & signatures card lists what waits on YOU. They differ by
    // design, so the tile says exactly what it counts.
    explanation: t(
      "home.today.pendingApprovalsExplained",
      "Proposals, contracts, invoices and change orders awaiting approval. Document reviews and signature requests are listed in Approvals & signatures."
    ),
    drillTo: "/approval"
  }
]

/**
 * §4's one compact summary band.
 *
 * One surface divided by hairlines rather than four outlined cards — the same
 * rule the metric grid follows, so the page does not read as a stack of
 * identical boxes. A count that did not resolve is left out entirely.
 */
export const HomeTodayBand = ({
  counts,
  label,
  variant = "band"
}: {
  counts: TodayCount[]
  /** The band's accessible name; defaults to the At a glance summary. */
  label?: string
  /**
   * `tiles`: each count its own small card with a gutter between — the
   * page's leading KPI strip. `band`: one surface divided by hairlines.
   */
  variant?: "band" | "tiles"
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const shown = counts.filter((count) => count.value !== null)
  if (!shown.length) return null

  return (
    <section
      style={
        variant === "tiles"
          ? undefined
          : { ...homeCardStyle, overflow: "hidden" }
      }
      aria-label={
        label ?? t("home.today.bandLabel", "Today at a glance summary")
      }
    >
      <div
        className={
          variant === "tiles"
            ? "home-today-band home-today-band--tiles"
            : "home-today-band"
        }
        style={{ ["--home-metric-border" as string]: homeColor.border }}
      >
        {shown.map((count) => {
          const Glyph = count.icon
          const interactive = Boolean(count.drillTo)
          return (
            <div
              key={count.key}
              className="home-today-band__cell"
              data-home-metric-cell
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={interactive ? () => navigate(count.drillTo!) : undefined}
              onKeyDown={
                interactive
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault()
                        navigate(count.drillTo!)
                      }
                    }
                  : undefined
              }
              style={{ cursor: interactive ? "pointer" : undefined }}
            >
              <span
                aria-hidden
                className="home-today-band__glyph"
                style={{ background: count.tint.bg, color: count.tint.fg }}
              >
                <Glyph width={18} height={18} />
              </span>
              <span className="min-w-0">
                <span
                  style={{
                    ...homeText.metricValue,
                    ...TABULAR,
                    color: count.valueColor ?? homeColor.ink,
                    display: "block"
                  }}
                >
                  {count.display ?? count.value}
                </span>
                <span
                  style={{
                    ...homeText.label,
                    color: homeColor.inkMuted,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4
                  }}
                >
                  {count.label}
                  {count.explanation ? (
                    <Tooltip title={count.explanation}>
                      {/* A real button: a tooltip on a non-focusable span is
                          unreachable without a mouse, and this one carries
                          the only definition of what the count means. */}
                      <button
                        type="button"
                        aria-label={count.explanation}
                        className="home-today-band__hint"
                      >
                        <InfoIcon width={13} height={13} aria-hidden />
                      </button>
                    </Tooltip>
                  ) : null}
                </span>
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}

/**
 * §6's compact financial band: Revenue, Outstanding receivables, Overdue
 * payments. Same band component as Today's, because they are the same object
 * — a row of headline figures over one surface — and two near-identical
 * components would drift.
 *
 * Revenue and receivables are period/às-of figures the catalog already
 * resolves. Overdue payments has NO metric in the widget registry, so its
 * tile is absent until one exists rather than being shown as a confident
 * zero.
 */
export const financialBandSpecs = (
  t: (key: string, fallback: string) => string,
  values: {
    revenue: number | null
    outstandingReceivables: number | null
    overdueReceivables: number | null
    currency?: string
  }
): TodayCount[] => {
  const money = (amount: number): string =>
    budgetFormat(amount, { currency: values.currency })

  return [
    {
      key: "revenue",
      label: t("home.financial.revenue", "Revenue"),
      value: values.revenue,
      display: values.revenue == null ? undefined : money(values.revenue),
      icon: DollarSignIcon,
      tint: homePastel.mint,
      drillTo: "/invoices"
    },
    {
      key: "outstanding_receivables",
      label: t("home.financial.receivables", "Outstanding receivables"),
      value: values.outstandingReceivables,
      display:
        values.outstandingReceivables == null
          ? undefined
          : money(values.outstandingReceivables),
      icon: ReceiptTextIcon,
      tint: homePastel.sky,
      explanation: t(
        "home.financial.receivablesExplained",
        "Invoiced and not yet paid, as of today. Not narrowed by the selected period."
      ),
      drillTo: "/invoices"
    },
    {
      key: "overdue_receivables",
      label: t("home.financial.overdue", "Overdue receivables"),
      value: values.overdueReceivables,
      display:
        values.overdueReceivables == null
          ? undefined
          : money(values.overdueReceivables),
      icon: CircleAlertIcon,
      tint: RED,
      // Red only when something IS overdue: a red zero reads as an alarm.
      valueColor:
        (values.overdueReceivables ?? 0) > 0 ? homeColor.delayedInk : undefined,
      explanation: t(
        "home.financial.overdueExplained",
        "The part of outstanding receivables past its due date, as of today. Not narrowed by the selected period."
      ),
      drillTo: "/invoices"
    }
  ]
}
